import { Directive, input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BehaviorSubject } from 'rxjs';

import { Sort, SortDir } from './';

type OnChangeCallback = (value: string | Sort) => void;
type OnTouchedCallback = () => void;

@Directive({
  selector: '[iwSort]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SortDirective,
      multi: true,
    },
  ],
})
export class SortDirective implements ControlValueAccessor {
  private _subject = new BehaviorSubject<Sort>(null);
  private propagateChange: OnChangeCallback;
  private propagateTouch: OnTouchedCallback;

  asString = input(true);

  sort$ = this._subject.asObservable();

  writeValue(value: string | Sort): void {
    this._subject.next(
      typeof value === 'string' ? this._parseValue(value) : value,
    );
  }

  registerOnChange(fn: OnChangeCallback): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: OnTouchedCallback): void {
    this.propagateTouch = fn;
  }

  handleSort(field: string): void {
    const currentSort = this._subject.getValue();
    const isSameColumn = field === currentSort?.field;

    let dir: SortDir;

    if (isSameColumn) {
      dir = currentSort.dir === 'asc' ? 'desc' : 'asc'; // toggle
    } else {
      dir = 'asc'; // Use ascending sorting by default
    }

    const sort: Sort = { field, dir };

    this._subject.next(sort);
    this.propagateChange(this.asString() ? this._serializeSort(sort) : sort);
    this.propagateTouch();
  }

  private _parseValue(value: string | null): Sort {
    if (!value) {
      return null;
    }

    if (value.startsWith('-')) {
      return { field: value.slice(1), dir: 'desc' };
    } else {
      return { field: value, dir: 'asc' };
    }
  }

  private _serializeSort({ field, dir }: Sort): string {
    return dir === 'asc' ? field : `-${field}`;
  }
}
